/**
 * Park-aware "You may also like" - see sections/related-products.liquid.
 *
 * <park-recommendations> (product page): records this view, then, once the row
 * is near the viewport, re-picks it with items from parks the shopper recently
 * browsed, tops it up from Shopify's own recommendations if it is still short,
 * and fills the optional "Recently viewed" row.
 * <park-history> (park collection page): records that the park was viewed.
 *
 * History stays in this browser only: localStorage "osp-viewed" holds the 30
 * most recent views as { h: product handle ("" for a park page), p: [park
 * collection handles], t: timestamp }. If storage is unavailable (private
 * window, blocked site data) the server-rendered row simply stays as it is.
 *
 * One exception: for a SIGNED-IN customer, once per browser session, the park
 * handles are posted to the app proxy at /apps/parks/track, which records them
 * on that customer as custom.parks_browsed (see oprd-parks-proxy). Nothing is
 * posted for signed-out visitors, and nothing is posted if the shopper has
 * declined analytics in the privacy banner. Park handles only - never products,
 * pages or timestamps.
 */
(() => {
  const KEY = 'osp-viewed';
  const KEEP = 30;

  function readHistory() {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function recordView(h, parks) {
    const same = (e) => (h ? e.h === h : !e.h && e.p?.[0] === parks[0]);
    const list = readHistory().filter((e) => !same(e));
    list.unshift({ h, p: parks, t: Date.now() });
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, KEEP)));
    } catch (e) {}
  }

  const split = (s) => (s || '').split(',').filter(Boolean);

  const SENT = 'osp-browsed-sent';
  function analyticsAllowed() {
    try {
      const api = window.Shopify?.customerPrivacy;
      // No banner on the store: nothing has been declined.
      return api?.analyticsProcessingAllowed ? api.analyticsProcessingAllowed() : true;
    } catch (e) {
      return true;
    }
  }
  /** Signed-in only, once per session: tell the shop which parks were looked at. */
  function reportBrowsed(root, parks) {
    if (!parks.length || !analyticsAllowed()) return;
    try {
      if (sessionStorage.getItem(SENT)) return;
      sessionStorage.setItem(SENT, '1');
    } catch (e) {
      return; // no session storage: don't risk posting on every page view
    }
    const url = `${root.replace(/\/$/, '')}/apps/parks/track`;
    fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({parks: parks.slice(0, 30)}),
      keepalive: true,
    }).catch(() => {});
  }
  /** Every park in this browser's history, most recent first. */
  function browsedParks(extra) {
    const out = [...extra];
    for (const e of readHistory()) for (const p of e.p || []) if (!out.includes(p)) out.push(p);
    return out;
  }

  async function fetchCards(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll('li[data-handle]')];
    } catch (e) {
      return [];
    }
  }

  class ParkHistory extends HTMLElement {
    connectedCallback() {
      const parks = split(this.dataset.parks);
      if (!parks.length) return;
      recordView('', parks);
      if (this.dataset.customer) reportBrowsed(this.dataset.root || '/', browsedParks(parks));
    }
  }

  class ParkRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.pool) return; // already set up (element re-attached)
      this.parks = split(this.dataset.parks);
      // Server order, captured before any re-pick moves cards around.
      this.pool = [...this.querySelector('[data-grid]').children];
      this.history = readHistory(); // read before this view is added
      recordView(this.dataset.handle, this.parks);
      if (this.dataset.customer) reportBrowsed(this.dataset.root, browsedParks(this.parks));
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          this.build().catch((e) => console.error(e));
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      io.observe(this);
    }

    url(path) {
      return `${this.dataset.root.replace(/\/$/, '')}/${path}`;
    }

    // Tier 2: cards from parks this shopper looked at, most recent park first,
    // skipping products already viewed; this product's type comes first.
    async browsed() {
      const viewed = new Set(this.history.map((e) => e.h).filter(Boolean));
      const parks = [];
      for (const e of this.history) {
        for (const p of e.p || []) if (!this.parks.includes(p) && !parks.includes(p)) parks.push(p);
      }
      if (!parks.length) return [];
      const perPark = await Promise.all(
        parks.slice(0, 3).map(async (p) => {
          const cards = (await fetchCards(this.url(`collections/${encodeURIComponent(p)}?view=card-fragment`))).filter(
            (li) => !viewed.has(li.dataset.handle)
          );
          const same = cards.filter((li) => li.dataset.type === this.dataset.type);
          return [...same, ...cards.filter((li) => !same.includes(li))];
        })
      );
      // Interleave, so every browsed park gets a turn before any gets a second.
      const out = [];
      for (let i = 0; perPark.some((c) => c[i]); i++) for (const c of perPark) if (c[i]) out.push(c[i]);
      return out;
    }

    engine() {
      const d = this.dataset;
      return fetchCards(`${d.recommendationsUrl}?product_id=${d.productId}&limit=10&section_id=${d.sectionId}`);
    }

    async build() {
      if (this.built) return;
      this.built = true;
      const d = this.dataset;
      const slots = Number(d.slots);
      const parkMax = Number(d.parkMax);
      const historyMax = Number(d.historyMax);
      const grid = this.querySelector('[data-grid]');
      const all = this.pool;
      const park = all.filter((li) => li.dataset.tier === 'park');
      const region = all.filter((li) => li.dataset.tier === 'region');
      const browsed = historyMax > 0 ? await this.browsed() : [];

      // Same arithmetic as the Liquid default, plus the browsed tier.
      const taken = new Set([d.handle]);
      let count = 0;
      const pick = (from, n) => {
        const out = [];
        for (const li of from) {
          if (out.length >= n || count >= slots) break;
          if (taken.has(li.dataset.handle)) continue;
          taken.add(li.dataset.handle);
          out.push(li);
          count++;
        }
        return out;
      };
      const P = pick(park, parkMax);
      const B = pick(browsed, historyMax);
      P.push(...pick(park, historyMax - B.length)); // unused browsing slots go back to the park
      const R = pick(region, slots);
      P.push(...pick(park, slots));
      B.push(...pick(browsed, slots));
      const E = count < slots ? pick(await this.engine(), slots) : [];
      const chosen = [...P, ...B, ...R, ...E];

      const shown = all.filter((li) => !li.hidden);
      if (chosen.length !== shown.length || chosen.some((li, i) => li !== shown[i])) {
        for (const li of all) li.hidden = true;
        for (const li of chosen) {
          li.hidden = false;
          grid.append(li);
        }
      }
      this.querySelector('[data-heading]').hidden = chosen.length === 0;
      await this.recent();
    }

    async recent() {
      const box = this.querySelector('[data-recent]');
      if (!box) return;
      const handles = [];
      for (const e of this.history) {
        if (e.h && e.h !== this.dataset.handle && !handles.includes(e.h)) handles.push(e.h);
      }
      if (!handles.length) return;
      const cards = (
        await Promise.all(
          handles
            .slice(0, Number(this.dataset.slots))
            .map((h) => fetchCards(this.url(`products/${encodeURIComponent(h)}?view=card-fragment`)))
        )
      ).flat();
      if (!cards.length) return;
      box.querySelector('ul').append(...cards);
      box.hidden = false;
    }
  }

  if (!customElements.get('park-history')) customElements.define('park-history', ParkHistory);
  if (!customElements.get('park-recommendations')) customElements.define('park-recommendations', ParkRecommendations);
})();
